import componentRegistry from '../registry.js';

const toPixels = (n) => n + 'px';
const ATTRIBUTES = { IS_COLLAPSED: 'data-is-collapsed' };
const EVENTS = { CLICK: 'click' };
const CLASSES = {
  EXPANDED: 'expanded',
  JS: {
    EXPANDER: '.js-simpleExpander',
    EXPANDER_CONTENT_MARKER: '.js-simpleExpander-content',
    EXPANDED_TRIGGER_ELEMLENT: '.js-simpleExpander-trigger',
    EXPANDED_ICON_ELEMENT: '.js-simpleExpander-icon',
    EXPANDER_LABEL_ELEMENT: '.js-simpleExpander-label',
  },
};
const SIZE = {
  MIN: '0',
  NOTHING: 'unset',
  INFINITE: toPixels(1000000000),
};
const ICONS = { UP: 'icon-chevron-up', DOWN: 'icon-chevron-down' };
const POSITIONS = { ABSOLUTE: 'absolute', INHERITS: 'inherits' };

export default class SimpleExpander {
  #originalHeight;
  #content;
  #transitionDuration;
  #isUsed;
  #icon;
  #label;

  constructor({ el }) {
    this.el = el;
    this.#content = this.el.querySelector(CLASSES.JS.EXPANDER_CONTENT_MARKER);
    this.triggerOnClick = this.el.querySelector(CLASSES.JS.EXPANDED_TRIGGER_ELEMLENT);
    this.#icon = this.el.querySelector(CLASSES.JS.EXPANDED_ICON_ELEMENT);
    this.#label = this.el.querySelector(CLASSES.JS.EXPANDER_LABEL_ELEMENT);
    this.#transitionDuration = parseFloat(window.getComputedStyle(this.#content).transitionDuration);
    this.#originalHeight = this.#content.clientHeight;
    this.el.dataset.isCollapsed = this.el.dataset.isCollapsed ?? true;
    this.#isUsed = false;

    this.initialize();
  }

  initialize() {
    if (this.el.dataset.isCollapsed === 'true') {
      this.#content.style.maxHeight = SIZE.MIN;
      this.#icon?.classList.add(ICONS.DOWN);
      if (this.#label) this.#label.innerHTML = this.#label.dataset.expandLabel;
    } else {
      this.#content.style.maxHeight = SIZE.NOTHING;
      this.#icon?.classList.add(ICONS.UP);
      if (this.#label) this.#label.innerHTML = this.#label.dataset.collapseLabel;
    }
    new MutationObserver(this.resize.bind(this)).observe(this.el, {
      childList: true,
      subtree: false,
      attributes: false,
    });
    new MutationObserver(this.onChangeState.bind(this)).observe(this.el, {
      attributes: true,
      attributeFilter: [ATTRIBUTES.IS_COLLAPSED],
      attributeOldValue: true,
    });
    if (this.triggerOnClick) this.triggerOnClick.addEventListener(EVENTS.CLICK, this.onClick.bind(this));
    else this.el.addEventListener(EVENTS.CLICK, this.onClick.bind(this));
  }

  resize() {
    var { isCollapsed } = this.el.dataset;

    if (isCollapsed === 'true') {
      this.#content.style.position = POSITIONS.ABSOLUTE;
      this.#content.style.left = SIZE.INFINITE;
      this.#content.style.maxHeight = SIZE.NOTHING;
      this.#originalHeight = this.#content.clientHeight;
      this.#content.style.maxHeight = SIZE.MIN;
      this.#content.style.position = POSITIONS.INHERITS;
      this.#content.style.left = SIZE.NOTHING;
    }
  }

  onChangeState(mutationRecordArray) {
    for (const record of mutationRecordArray) {
      if (record.attributeName === ATTRIBUTES.IS_COLLAPSED) {
        var { isCollapsed } = this.el.dataset;

        if (record.oldValue === isCollapsed) continue;
        if (isCollapsed === 'true') this.close();
        else this.open();
      }
    }
  }

  onClick(e) {
    if (this.#isUsed) return;
    var { isCollapsed } = this.el.dataset;

    if (isCollapsed === 'true') this.el.dataset.isCollapsed = false;
    else this.el.dataset.isCollapsed = true;
  }

  open() {
    if (this.el.dataset.accordion?.length) {
      var parent = this.el.dataset.accordion;
      var accordionParent = document.querySelector('.' + parent);
      var expanders = accordionParent.querySelectorAll(CLASSES.JS.EXPANDER);
      expanders.forEach((expander) => {
        if (expander != this.el) expander.dataset.isCollapsed = true;
      });
    }
    if (this.#isUsed) return;
    if (this.#label) this.#label.innerHTML = this.#label.dataset.collapseLabel;
    this.#isUsed = true;
    this.#content.style.maxHeight = toPixels(this.#originalHeight);
    this.#icon?.classList.replace(ICONS.DOWN, ICONS.UP);
    setTimeout(() => {
      this.#isUsed = false;
      this.#content.classList.add(CLASSES.EXPANDED);
      this.#content.ariaExpanded = 'true';
    }, this.#transitionDuration * 1000);
  }

  close() {
    if (this.#isUsed) return;
    if (this.#label) this.#label.innerHTML = this.#label.dataset.expandLabel;
    this.#isUsed = true;
    this.#originalHeight = this.#content.clientHeight;
    this.#content.style.maxHeight = toPixels(this.#originalHeight);
    this.#content.classList.remove(CLASSES.EXPANDED);
    this.#content.ariaExpanded = 'false';
    this.#icon?.classList.replace(ICONS.UP, ICONS.DOWN);
    setTimeout(() => {
      this.#content.style.maxHeight = SIZE.MIN;
      this.#isUsed = false;
    }, 100);
  }
}

componentRegistry.define('js-simpleExpander', SimpleExpander);
